import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaChartBar, FaTrashAlt, FaUserEdit } from 'react-icons/fa';
import { FaListAlt, FaTabletAlt, FaCog } from 'react-icons/fa';
import Select from 'react-select'
import * as Motion from 'motion/react';
import DashboardList, { DashboardUserDeviceListRenderComponent } from '../../components/DashboardList';
import Loader from '../../components/Loaders';
import { UserAvatar } from '../../components/UserProfileItem';
import UserSessionsTable from '../../components/UserSessionsTable';
import useDialogBox from '../../hooks/useDialogBox';
import useSocket from '../../hooks/useSocket';
import { deleteUser, getUser, updateUser, updateUserProtocol } from '../../api/v1/users';
import { getSessionsForUser } from '../../api/v1/sessions';
import { registerDeviceWithCode } from '../../api/v1/devices';
import { getLeagues } from '../../api/v1/leagues';
import { getProtocols, getTemplateProtocols } from '../../api/v1/protocols';
import { animationStaggeredItem } from '../../animations';
import '../../styles/screen.scss';

const { motion } = Motion;


const User = () => {
  const formRef = useRef(null);
  const [user, setUser] = useState({});
  const [sessions, setSessions] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [responseToast, setResponseToast] = useState();
  const [protocolId, setProtocolId] = useState('-1');
  const [isLoading, setIsLoading] = useState(true);
  const { resetDialogBox, setDialogBox } = useDialogBox();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { isConnected, send, subscribe } = useSocket();

  const [connectedIds, setConnectedIds] = useState(new Set());

  const loadUser = async (userId) => {
    const result = await getUser(userId);

    setUser(result?.data);
    setProtocolId(result?.data.protocolId || '');
  };

  const loadProtocols = async (userId) => {
    const result = await getProtocols(userId);

    setProtocols(result?.data);
  };

  const loadSessions = async (userId) => {
    const result = await getSessionsForUser(userId);

    setSessions(result?.data);
  };

  const confirmDeleteUser = async (userId) => {
    setDialogBox({
      title: 'Delete User',
      content: <p>Do you want to delete user <b>{user?.userName || userId}</b>?</p>,
      actions: [
        { title: 'Delete User', type: 'danger', action: async () => await handleDeleteUser(userId) },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  const registerDevice = async () => {
    setDialogBox({
      title: 'Register Device To User',
      content: <RegisterDeviceForm ref={formRef}/>,
      actions: [
        { title: 'Register Device', type: 'primary', action: async () => await registerDeviceSubmit(userId) },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  const registerDeviceSubmit = async () => {
    const { registrationCode } = formRef.current?.getValues();

    const payload = {
      userId: userId,
      registrationCode: registrationCode,
    };

    resetDialogBox();
    await registerDeviceWithCode(payload);
    await loadUser(userId);
  };

  const handleDeleteUser = async (userId) => {
    await deleteUser(userId);
    resetDialogBox();

    navigate(-1);
  };

  const navigateToProtocol = async (protocolId) => {
    navigate(`/protocols/${protocolId}`);
  };

  const updateProtocol = async (protocolId) => {
    const payload = {
      protocolId: protocolId,
    };

    const response = await updateUserProtocol(userId, payload);

    await loadUser(userId);

    setResponseToast(response?.data.message);
  };

  const getUserDevices = (u, connectedDevices) => {
    if (u?.Devices?.length) {
       return u?.Devices.map((device) => ({
        userName: user?.name,
        deviceType: device?.deviceType,
        deviceId: device?.deviceId,
        isConnected: connectedDevices?.has(device?.deviceId)
      }));
    }

    return [];
  }

  const editUserSubmit = async () => {
    const { leagues, userName, protocol } = formRef.current?.getValues();

    if (!userName.length) {
      alert('Please enter a user name');
    }
    else if (protocol === '-1') {
      alert('Please select a protocol');
    }
    else {
      await updateUser(userId, {
        userName,
        protocol,
        leagues
      });

      await loadUser(userId);
      resetDialogBox();
    }
  };

  const editUser = async (u) => {
    setDialogBox({
      title: 'Edit User',
      content: <EditUserForm ref={formRef} user={u}/>,
      actions: [
        { title: 'Save User', type: 'primary', action: () => editUserSubmit() },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  useEffect(() => {
    setTimeout(() => {
      setResponseToast(null);
    }, 5000)
  }, [responseToast]);

  useEffect(() => {
    (async () => {
      await loadSessions(userId);
      setIsLoading(false);
    })();
  }, [user]);

  useEffect(() => {
    (async () => {
      await loadUser(userId);
      await loadProtocols();
    })();
  }, [userId]);

  useEffect(() => {
    if (user && isConnected) {
      const unsubscribe = subscribe(`devicesResponse`, (message) => {
        setConnectedIds(new Set(message.payload.map(d => d.deviceId)));
      });

      send(JSON.stringify({ channel: 'devicesRequest' }));

      return unsubscribe;
    }
  }, [user, isConnected]);

  return (
    <>
      <Motion.AnimatePresence>
        {
          isLoading && <Loader />
        }
      </Motion.AnimatePresence>

      {
        responseToast && <div className={'response-toast'}>{responseToast}</div>
      }

      <div className="screen-navigation">
        <button className="screen-back-button" onClick={() => navigate(-1)}>&larr; Go Back</button>
      </div>

      <motion.div variants={animationStaggeredItem} className="screen-header">
        <div className="screen-title-wrapper">
          <UserAvatar userName={user?.userName}/>

          <div>
            <h2 className="screen-title">
              {user?.userName}
            </h2>

            <p className="screen-subtitle">
              <b>User ID:</b> {user?.id}
            </p>
          </div>
        </div>

        <button className="screen-header-button secondary" onClick={() => navigate(`/users/${user.id}/statistics`)}>
          <FaChartBar/>
          Stats
        </button>

        <button className="screen-header-button secondary" onClick={() => editUser(user)}>
          <FaUserEdit/>
          Edit User
        </button>

        <button className="screen-header-button danger" onClick={() => confirmDeleteUser(userId)}>
          <FaTrashAlt/>Delete User
        </button>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-row">
        <div className="screen-section user-device-list">
          <div className="section-heading-wrapper">
            <h3 className="section-heading">
              <FaTabletAlt/>
              Devices ({getUserDevices(user, connectedIds).length})
            </h3>

            <button className="screen-header-button secondary" onClick={registerDevice}>Register Device</button>
          </div>

          {
            getUserDevices(user).length > 0 ?
              <DashboardList items={getUserDevices(user, connectedIds)} RenderComponent={DashboardUserDeviceListRenderComponent}/> :
              <p className="text-center text-secondary">No devices</p>
          }
        </div>

        <div className="screen-section">
          <div className="section-heading-wrapper">
            <h3 className="section-heading">
              <FaCog/>
              Protocol
            </h3>

            {
              user?.protocolId &&
              <button className="screen-header-button secondary"
                      onClick={() => navigateToProtocol(user?.protocolId)}>View Active Protocol</button>
            }
          </div>

          <div className="field-wrapper">
            <select value={protocolId} onChange={(e) => updateProtocol(e.target.value)}>
              <option disabled value={''} key={-1}>Select A Protocol</option>
              {
                protocols.map((protocol, index) => {
                  return <option value={protocol.id} key={index}>{protocol?.name || protocol?.id}</option>
                })
              }
            </select>
          </div>
        </div>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-section flex-1 scroll-overflow">
        <div className="section-heading-wrapper">
          <h3 className="section-heading">
            <FaListAlt/>
            Sessions ({sessions.length})
          </h3>
        </div>

        {
          sessions.length > 0 ?
            <UserSessionsTable sessions={sessions}/> :
            <p className="text-vertically-align text-center text-secondary">No sessions</p>
        }
      </motion.div>
    </>
  )
};


const RegisterDeviceForm = forwardRef((props, ref) => {
  const [registrationCode, setRegistrationCode] = useState('');

  useImperativeHandle(ref, () => ({
    getValues,
  }));

  const getValues = () => {
    return { registrationCode };
  };

  return (
    <div className="modal-form">
      <div className="form-group">
        <label>Registration Code</label>
        <input placeholder="Registration Code" maxLength={6} value={registrationCode}
               onChange={(e) => setRegistrationCode(e.target.value)}/>
      </div>
    </div>
  );
});


const EditUserForm = forwardRef((props, ref) => {
  const [userName, setUserName] = useState(props?.user?.userName);
  const [protocol, setProtocol] = useState(props?.user?.Protocol?.id);
  const [templateProtocols, setTemplateProtocols] = useState([]);
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [selectedLeagues, setSelectedLeagues] = useState([]);

  const getValues = () => {
    const leagues = selectedLeagues ? selectedLeagues.map(opt => opt.value) : [];
    return { leagues, userName, protocol };
  };

  const handleLeagueSelectChange = (selectedOptions) => {
    setSelectedLeagues(selectedOptions || []);
  };

  useEffect(() => {
    (async () => {
      const result = await getTemplateProtocols();
      setTemplateProtocols(result?.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const result = await getLeagues();
      setAvailableLeagues(result?.data);
    })();
  }, []);

  useEffect(() => {
    const userExistingLeagues = props?.user?.Leagues ? props?.user?.Leagues.map(opt => ({
      value: opt.id,
      label: opt.displayName
    })) : [];

    setSelectedLeagues(userExistingLeagues);
  }, []);

  useImperativeHandle(ref, () => ({
    getValues,
  }));

  return (
    <div className="modal-form">
      <div className="form-group">
        <label>User Name</label>
        <input placeholder="User Name" value={userName} onChange={(e) => setUserName(e.target.value)}/>
      </div>

      <div className="form-group">
        <label>Protocol</label>
        <select value={protocol} onChange={(e) => setProtocol(e.target.value)}>
          <option disabled key={-1} value={'-1'}>Select a protocol</option>
          {
            templateProtocols?.map((templateProtocol, index) => (
              <option key={index} value={templateProtocol.id}>{templateProtocol.name}</option>
            ))
          }
        </select>
      </div>

      <div className="form-group">
        <label>Leagues</label>
        <Select
          value={selectedLeagues}
          onChange={handleLeagueSelectChange}
          isMulti={true}
          placeholder={'Select leagues'}
          options={
            availableLeagues?.map((availableLeague, index) => ({
              value: availableLeague?.id,
              label: availableLeague?.displayName,
            }))
          }
          styles={{
            container: (provided) => ({
              ...provided,
              borderRadius: '0.4rem',
              borderColor: '#0c2659',
              fontFamily: '"Open Sans", sans-serif',
              fontSize: '14px',
              fontWeight: 500,
            }),
            control: (provided, state) => ({
              ...provided,
              backgroundColor: '#06132c',
              borderColor: '#091d42',
              color: 'white',
              cursor: 'text',
              padding: '0.4rem',
              '&:hover': {
                borderColor: '#091d42',
              },
            }),
            menu: (provided) => ({
              ...provided,
              backgroundColor: '#06132c',
              borderRadius: '0.6rem',
            }),
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#3b82f6',
              borderRadius: '0.4rem',
              padding: '0.2rem 0.4rem',
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#ffffff',
              fontSize: '0.8rem',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected
                ? '#ffffff'
                : state.isFocused
                  ? '#3b82f6'
                  : '#06132c',
              cursor: 'pointer',
              color: state.isSelected
                ? 'white'
                : state.isFocused
                  ? '#ffffff' // Text color on hover
                  : '#white',
            }),
            input: (provided) => ({
              ...provided,
              backgroundColor: '#091d42',
              color: '#ffffff',
            }),
            singleValue: (provided) => ({
              ...provided,
              color: '#3b82f6', // Keeps the final selected text Blue
            }),
          }}
        />
      </div>
    </div>
  );
});

export default User;
